import * as React from "react";
import { Globe, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { useContainers } from "@/hooks/useContainers";
import { cn } from "@/lib/utils";

interface SelectContainerDialogProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  addOnName: string;
  pending: boolean;
  onConfirm: (containerId: number) => void;
}

/**
 * Modal used for Container-scoped add-on purchases. Lists the user's
 * domains; clicking one + Continue hands the chosen container ID up to
 * the caller, which fires the purchase-intent mutation.
 */
export function SelectContainerDialog({
  open,
  onOpenChange,
  addOnName,
  pending,
  onConfirm
}: SelectContainerDialogProps) {
  const { data, isLoading, error } = useContainers();
  const [selectedId, setSelectedId] = React.useState<number | null>(null);

  // Reset selection whenever the dialog opens — never leak the previous
  // choice into a second purchase.
  React.useEffect(() => {
    if (open) setSelectedId(null);
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Which domain is this for?</DialogTitle>
          <DialogDescription>
            {addOnName} will be attached to the domain you pick.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[40vh] overflow-y-auto">
          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-14 w-full" />
              ))}
            </div>
          ) : error ? (
            <p className="py-6 text-center text-sm text-destructive">
              Couldn't load your domains.
            </p>
          ) : !data || data.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              You don't have any domains yet — add one first, then come back.
            </p>
          ) : (
            <ul className="space-y-2">
              {data.map(c => {
                const active = selectedId === c.id;
                return (
                  <li key={c.id}>
                    <button
                      type="button"
                      onClick={() => setSelectedId(c.id)}
                      className={cn(
                        "flex w-full items-center gap-3 rounded-lg border p-3 text-left transition-colors",
                        active
                          ? "border-brand-accent bg-brand-accentSoft ring-2 ring-brand-accentLighter"
                          : "border-border hover:bg-muted"
                      )}
                    >
                      <div
                        className={cn(
                          "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg",
                          active ? "bg-primary text-primary-foreground" : "bg-brand-accentSoft text-primary"
                        )}
                      >
                        <Globe className="h-4 w-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium">{c.domain_name}</p>
                        <p className="text-xs text-muted-foreground">
                          {c.mailboxes_used}/{c.mailboxes_allocated || "—"} mailboxes
                          {c.services_count > 0 ? ` · ${c.services_count} services` : ""}
                        </p>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={pending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            disabled={selectedId === null || pending}
            onClick={() => selectedId !== null && onConfirm(selectedId)}
          >
            {pending && <Loader2 className="h-4 w-4 animate-spin" />}
            Continue to checkout
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
